import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "react-router";

import type { Route } from "./+types/root";
import styles from "./tailwind.css?inline";
import AppLayout from "./components/Layout";
import { getOptionalUser } from "./lib/auth.server";
import { isAdminEmail } from "./lib/admin.server";
import { isGuestEmail } from "./lib/guest.server";
import type { AppEnv } from "./lib/env.server";

import interLatin400 from '@fontsource/inter/files/inter-latin-400-normal.woff2?url';
import interLatin600 from '@fontsource/inter/files/inter-latin-600-normal.woff2?url';
import jbMonoLatin400 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-400-normal.woff2?url';
import jbMonoLatin500 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-500-normal.woff2?url';

export const links: Route.LinksFunction = () => [
  { rel: "preload", href: interLatin400, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: interLatin600, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: jbMonoLatin400, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: jbMonoLatin500, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
];

export async function loader({ request, context }: Route.LoaderArgs) {
  const env = context.cloudflare.env as AppEnv;
  const ctx = context.cloudflare.ctx;

  try {
    const user = await getOptionalUser(request, env, ctx);
    return {
      isLoggedIn: !!user,
      userEmail: user?.email ?? "",
      isAdmin: user ? isAdminEmail(env.ADMIN_EMAILS, user.email) : false,
      isGuest: user ? isGuestEmail(user.email) : false,
    };
  } catch (err) {
    // E8: Root loader resilience — KV/D1 blip should not crash public pages
    console.error("[root] Auth check failed:", err);
    return {
      isLoggedIn: false,
      userEmail: "",
      isAdmin: false,
      isGuest: false,
    };
  }
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <Meta />
        <Links />
      </head>
      <body className="bg-bg-primary text-text-primary antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <AppLayout />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  console.error(error);
  let message = "Something went wrong";
  let details = "An unexpected error occurred. Please try again.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Something went wrong";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center pt-16 px-6">
      <div className="text-center max-w-md">
        <h1 className="font-heading text-h1 text-text-primary mb-4">{message}</h1>
        <p className="font-body text-body text-text-secondary mb-6">{details}</p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/"
            className="font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-5 py-2.5 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline inline-block"
          >
            Go Home
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="font-heading text-sm font-medium border border-border-subtle text-text-primary px-5 py-2.5 rounded-md hover:bg-bg-elevated hover:border-border-active transition-all duration-200"
          >
            Try Again
          </button>
        </div>
        {stack && (
          <pre className="w-full p-4 overflow-x-auto text-left text-xs text-text-muted mt-8 bg-bg-surface rounded-lg border border-border-subtle">
            <code>{stack}</code>
          </pre>
        )}
      </div>
    </div>
  );
}
